/**
 * territory-scope.ts — the ONE decider for what a stored territory value MEANS, and the ONE
 * renderer for saying it to a user.
 *
 * WHY THIS FILE EXISTS. "Worldwide" used to be stored as NULL and NULL was read back as
 * worldwide, so "the whole world" and "nobody said anything" were the same bytes (audit
 * TERR-01). Phases 1-3 split them in the writers and in the database (`public.territory_scope`,
 * nine CHECK constraints, `license_deal_clearance_units.territory_scope GENERATED ALWAYS`). The
 * readers still collapse them, in SEVEN independent implementations that disagree about one
 * null — TERR-22, "the same NULL is rendered at least six different ways":
 *
 *   termDisplay.isWorldwide                            `t.length === 0` -> Worldwide
 *   termDisplay.summarizeTerritories                   no resolvable labels -> Worldwide
 *   termDisplay.formatTermValue                        no resolvable labels -> Worldwide
 *   licensingTrackPresentation.describeHolderTerritory  -> Worldwide
 *   lib/licensing/format.formatTerritories              -> "Not set"
 *   HolderStartMySidePanel / StartNegotiationPrecheckDialog -> Worldwide
 *   PublicRightsRows.summarizeTerritories               -> "Territories not stated"  <- correct
 *
 * This module is built from the last of those. Every other one becomes a caller.
 *
 * MIRROR CONTRACT. `territoryScope()` is the exact mirror of
 * `zenmode-player-built/backend/core/services/licensing/scope.py::territory_scope` and of the
 * SQL `public.territory_scope(text[])`; the display strings mirror
 * `backend/core/services/licensing/territory_display.py`, and `src/test/territory-scope.test.ts`
 * parses both Python files and fails if either drifts. When the three disagree, the database is
 * the authority. `laneTerritoryScope()` mirrors the generated column's CASE, read from the live
 * catalog 2026-09-22.
 */
import {
  getDisplaySelections,
  getTerritoryLabel,
  normalizeSelection,
} from "@/data/territory-display";
import { decomposeTerritoryPresets } from "@/data/territory-presets";

/* ------------------------------------------------------------------ */
/* Vocabulary                                                          */
/* ------------------------------------------------------------------ */

/** The ONE spelling of "everywhere" that is ever STORED. Mirrors `scope.TERRITORY_WORLDWIDE`. */
export const TERRITORY_WORLDWIDE = "worldwide";

/**
 * Every spelling accepted on READ. Mirrors `scope.WORLDWIDE_TERRITORY_TOKENS`.
 *
 * Deliberately WIDER than `normalizeSelection`, which matches only the literal "worldwide"
 * (data/territory-display.ts). A row stored as `ww` or `global` predates the canonicalizer and
 * still means the whole world; reading it as `specified` would print the fake ISO code "WW"
 * (TERR-42). Writers must keep collapsing to TERRITORY_WORLDWIDE — this set is read-side only.
 */
export const WORLDWIDE_TERRITORY_TOKENS: ReadonlySet<string> = new Set([
  "worldwide",
  "world",
  "ww",
  "global",
]);

/** What a territory ARRAY means. Three answers — `deal_scope` is not one of them; see below. */
export type TerritoryScope = "unstated" | "worldwide" | "specified";

/**
 * What a clearance LANE's territory means. The fourth value, `deal_scope`, is the
 * coordinator lane: the coordinator runs that side itself, so the lane covers whatever the
 * deal asks. It is NOT worldwide and NOT unstated, and it is NOT DERIVABLE FROM THE ARRAY —
 * the array is null in all ten live cases (TERR-08). It arrives on the wire, or from the
 * lane's `is_coordinator_handled` flag. That is why this type belongs to `laneTerritoryScope`,
 * which takes a row, and never to `territoryScope`, which takes an array.
 */
export type LaneTerritoryScope = TerritoryScope | "deal_scope";

/** Standalone line with no adjacent "Territories" label (public rights rows, cards). */
export const TERRITORY_UNSTATED_LABEL = "Territories not stated";
/** Beside a field label that already says "Territories" (holder-confirm, PDF key/value). */
export const TERRITORY_UNSTATED_INLINE_LABEL = "Not stated";
export const TERRITORY_WORLDWIDE_LABEL = "Worldwide";
/** Coordinator-handled lane. See LaneTerritoryScope. */
export const TERRITORY_DEAL_SCOPE_LABEL = "Covers the requested scope";

/* ------------------------------------------------------------------ */
/* The deciders                                                        */
/* ------------------------------------------------------------------ */

function cleanTokens(value: readonly string[] | null | undefined): string[] {
  if (!Array.isArray(value)) return [];
  const out: string[] = [];
  for (const raw of value) {
    if (typeof raw !== "string") continue;
    const token = raw.trim();
    if (token) out.push(token);
  }
  return out;
}

/**
 * What a stored territory ARRAY means: unstated | worldwide | specified.
 *
 *   null / undefined  -> "unstated"   (nobody has said — NOT a grant)
 *   []                -> "unstated"   (see note)
 *   ["  ", ""]        -> "unstated"
 *   ["worldwide"]     -> "worldwide"
 *   ["WW"] / ["global"] -> "worldwide"
 *   ["GR","CY"]       -> "specified"
 *   ["ZZ"]            -> "specified"  (stated but unrecognised is STILL stated)
 *
 * THE `[]` NOTE. The SQL mirror answers "specified" for `{}` because `{}` is not NULL. The
 * divergence is unreachable: `public.territories_wellformed` forbids `{}` in every territory
 * column (2026-09-20_territory_shape_lock.sql), so `[]` never exists in storage. It reaches the
 * frontend only as `read_model.py`'s flattened NULL, where "unstated" is the true answer.
 * Follow Python here, not SQL.
 *
 * THE BLANKS NOTE. `["  "]` answers "unstated" here because blanks are stripped BEFORE the
 * scope question, exactly as `territory_display.territory_label` does it in Python (raw
 * `scope.territory_scope(["  "])` would say "specified" — it tests the list, not the tokens).
 * The blank-only list is unstorable for the same reason `{}` is, so only the display path can
 * ever see one, and on that path a blank states nothing.
 */
export function territoryScope(value: readonly string[] | null | undefined): TerritoryScope {
  const tokens = cleanTokens(value);
  if (!tokens.length) return "unstated";
  if (tokens.some((t) => WORLDWIDE_TERRITORY_TOKENS.has(t.toLowerCase()))) return "worldwide";
  return "specified";
}

/** The minimum a caller must carry to ask the LANE question. */
export interface TerritoryScopeBearer {
  territories?: readonly string[] | null;
  /**
   * `license_deal_clearance_units.territory_scope` — GENERATED ALWAYS, never written.
   * Already on the request-detail and single-deal payloads (`select("*")` + `dict(unit)` in
   * `license_deal_read_service.hydrate_license_deals`); added to the deal-console payload in
   * Phase 4 unit U2. Prefer it: it is the database's own answer.
   *
   * Typed `| string` as well as the literal union because this is a WIRE field: an older or a
   * newer backend may put a value here that this build has no name for, and the guard below
   * decides what to do about it rather than the type pretending it cannot happen.
   */
  territory_scope?: LaneTerritoryScope | string | null;
  is_coordinator_handled?: boolean | null;
}

const LANE_SCOPES: ReadonlySet<string> = new Set([
  "unstated",
  "worldwide",
  "specified",
  "deal_scope",
]);

/**
 * What a clearance LANE's territory means — the only function that can answer "deal_scope".
 *
 * Reads the server's generated column verbatim when it is present. The fallback reproduces the
 * column's own CASE rung for rung, so an older payload degrades to the same answer instead of a
 * different one:
 *
 *   CASE WHEN territories IS NOT NULL THEN territory_scope(territories)
 *        WHEN is_coordinator_handled  THEN 'deal_scope'
 *        ELSE                              'unstated' END
 *
 * All 10 live coordinator lanes carry `territories = NULL` and no snapshot `territories` key,
 * so without this function they render "Worldwide" — a grant of the entire world asserted on
 * behalf of a holder who was never asked (TERR-08; measured live 2026-09-21).
 */
export function laneTerritoryScope(unit: TerritoryScopeBearer): LaneTerritoryScope {
  const wire = typeof unit.territory_scope === "string" ? unit.territory_scope.trim() : "";
  if (LANE_SCOPES.has(wire)) return wire as LaneTerritoryScope;
  if (unit.territories != null) return territoryScope(unit.territories);
  if (unit.is_coordinator_handled) return "deal_scope";
  return "unstated";
}

/* ------------------------------------------------------------------ */
/* The renderer                                                        */
/* ------------------------------------------------------------------ */

/**
 * The display labels for a territory value — preset-named, region-collapsed, `r:` expanded,
 * worldwide short-circuited, and SAFE ON AN UNRECOGNISED TOKEN.
 *
 *   unstated   -> []                       (the caller supplies the sentence)
 *   worldwide  -> ["Worldwide"]
 *   specified  -> ["UK & Ireland"]                    (a preset's codes, any order — DEC-070 §3.4)
 *              -> ["UK & Ireland", "North America"]   (two presets, whole)
 *              -> ["UK & Ireland", "France"]          (a preset and what is left)
 *              -> ["Europe", "United States"] / ["ZZ"] / ["Europe"] for `r:EU`
 *
 * THE PRESET RULE. A preset (`data/territory-presets.ts`) is stored as its plain country codes,
 * so the presets a stored set holds WHOLE read back by name and the rest is named as before
 * (`decomposeTerritoryPresets`: largest first, never breaking a whole region that folds into one
 * chip). A preset missing one code is not named. Presets are display only (never stored); the
 * backend's renderer (`territory_display.py`, used by the export and the email) names a stored set
 * the same way — presets held whole, then regions and countries by name.
 *
 * THE UNRECOGNISED-TOKEN RULE, and it is the whole reason this is one function.
 * `normalizeSelection` DROPS any token that is not a known ISO code or an `r:` region, so
 * `getTerritoryLabels(["ZZ"])` returns `[]`. Three call sites read that empty result as
 * "nothing on record" and print "Worldwide" (TERR-42) — a single typo in a narrow grant
 * rendering as a grant of the whole world. When the collapse yields nothing we name the raw
 * tokens instead (`getTerritoryLabel` returns its input unchanged for an unknown, and still
 * resolves `r:EU` -> "Europe"). A raw code is uninformative; it cannot be misread as a grant.
 *
 * RESIDUE, stated rather than hidden: a MIXED list whose known half resolves — `["GR","ZZ"]` —
 * renders only the known half ("Greece"), because the fallback is all-or-nothing. That
 * understates a grant, which is the safe direction and is the pre-existing behaviour of every
 * caller; widening it would change what five other Phase 4 units render. Tracked in the Phase 4
 * report, not silently fixed here.
 */
export function territoryLabels(value: readonly string[] | null | undefined): string[] {
  const scope = territoryScope(value);
  if (scope === "unstated") return [];
  if (scope === "worldwide") return [TERRITORY_WORLDWIDE_LABEL];
  const tokens = cleanTokens(value);
  const known = normalizeSelection(tokens);
  if (!known.length) return tokens.map((token) => getTerritoryLabel(token));
  const { presets, rest } = decomposeTerritoryPresets(known);
  // Largest first: whole regions, then the presets held whole, then single countries — so a
  // 219-country grant still opens on its regions, and "DACH" sits where three countries would.
  const selections = getDisplaySelections(rest);
  return [
    ...selections.filter((selection) => selection.type === "region").map((selection) => selection.label),
    ...presets.map((preset) => preset.label),
    ...selections.filter((selection) => selection.type !== "region").map((selection) => selection.label),
  ];
}

export interface TerritoryDisplay {
  scope: LaneTerritoryScope;
  /**
   * False ONLY for "unstated". Pair with the amber treatment the share cell already uses in
   * `RequestTrackOverviewGrid` — an unstated scope is a blocker to fill in, not a value to read
   * past. Mirrors `describeHolderShare`'s `{ label, stated }`.
   */
  stated: boolean;
  /** Bounded, for the cell. Never "" and never null — see the PDF blanking trap (TERR-14). */
  text: string;
  /** Unbounded, for `title=`. The cap hides, it never drops. */
  full: string;
  truncated: boolean;
  hiddenCount: number;
  /** [] for unstated and for deal_scope; ["Worldwide"]; or the collapsed list. */
  labels: string[];
}

export interface TerritoryDisplayOptions {
  /** 0 / omitted = no cap. */
  max?: number;
  /**
   * `splitForOverflow`'s clause (lib/licensing/term-overflow.ts): when the overflow would be a
   * single item, render the list whole — "+1 more" costs as much room as the item.
   * `describeHolderTerritory` uses it; `summarizeTerritories` does not. Both policies are
   * deliberate, so the option exists rather than one surface silently changing.
   */
  skipSingleOverflow?: boolean;
  /** Default TERRITORY_UNSTATED_LABEL; pass TERRITORY_UNSTATED_INLINE_LABEL beside a label. */
  unstatedLabel?: string;
}

function describe(
  scope: LaneTerritoryScope,
  value: readonly string[] | null | undefined,
  opts: TerritoryDisplayOptions,
): TerritoryDisplay {
  if (scope === "unstated") {
    const text = opts.unstatedLabel ?? TERRITORY_UNSTATED_LABEL;
    return { scope, stated: false, text, full: text, truncated: false, hiddenCount: 0, labels: [] };
  }
  if (scope === "deal_scope") {
    const text = TERRITORY_DEAL_SCOPE_LABEL;
    return { scope, stated: true, text, full: text, truncated: false, hiddenCount: 0, labels: [] };
  }
  const labels = scope === "worldwide" ? [TERRITORY_WORLDWIDE_LABEL] : territoryLabels(value);
  const full = labels.join(", ");
  const max = opts.max ?? 0;
  const limit = opts.skipSingleOverflow ? max + 1 : max;
  if (max <= 0 || labels.length <= limit) {
    return { scope, stated: true, text: full, full, truncated: false, hiddenCount: 0, labels };
  }
  const hiddenCount = labels.length - max;
  return {
    scope,
    stated: true,
    text: `${labels.slice(0, max).join(", ")} +${hiddenCount} more`,
    full,
    truncated: true,
    hiddenCount,
    labels,
  };
}

/** Render a territory ARRAY. Cannot produce "Covers the requested scope" — use the lane form. */
export function describeTerritories(
  value: readonly string[] | null | undefined,
  opts: TerritoryDisplayOptions = {},
): TerritoryDisplay {
  return describe(territoryScope(value), value, opts);
}

/** Render a clearance LANE, including the coordinator's fourth state. */
export function describeLaneTerritories(
  unit: TerritoryScopeBearer,
  opts: TerritoryDisplayOptions = {},
): TerritoryDisplay {
  return describe(laneTerritoryScope(unit), unit.territories ?? null, opts);
}

/* ------------------------------------------------------------------ */
/* Comparison                                                          */
/* ------------------------------------------------------------------ */

/**
 * Do two territory values MEAN the same thing? Scope first, then codes.
 *
 * This NEVER treats unstated as equal to worldwide: NULL means not stated and `["worldwide"]` is a
 * grant, two facts that no comparison may merge. The backend once folded them one way
 * (`territories_equivalent`, retired in Phase 6 — tasks/60 §4 item 1) to stop a legacy-NULL lane
 * triggering a coverage rewrite; on a screen that fold is the very confusion Phase 4 removed.
 *
 * Expands `r:` regions before comparing, which today's `termValuesEqual` does not: `["r:EU"]`
 * and its member codes DISPLAY identically and COMPARE unequal, so `deriveKeyTermRows` and
 * `deriveBaselineDivergence` flag a difference the formatter then refuses to render — two
 * "Worldwide" columns marked as diverging. Unresolvable tokens are kept, uppercased, rather
 * than dropped: two different typos must not compare equal.
 */
export function territoryTokensEqual(
  left: readonly string[] | null | undefined,
  right: readonly string[] | null | undefined,
): boolean {
  const ls = territoryScope(left);
  const rs = territoryScope(right);
  if (ls !== rs) return false;
  if (ls !== "specified") return true;
  return comparableKey(left) === comparableKey(right);
}

function comparableKey(value: readonly string[] | null | undefined): string {
  const tokens = cleanTokens(value);
  const resolved = normalizeSelection(tokens);
  const unresolved = tokens
    .filter((token) => normalizeSelection([token]).length === 0)
    .map((token) => token.toUpperCase());
  return [...new Set([...resolved, ...unresolved])].sort().join("|");
}

/* ------------------------------------------------------------------ */
/* Coverage (a predicate, not a formatter)                             */
/* ------------------------------------------------------------------ */

export type TerritoryCoverageVerdict = "covers" | "gap" | "unknown";

/**
 * Does `granted` cover `requested`? THREE-VALUED on purpose.
 *
 * `"unknown"` when the granting side has stated nothing: not a gap, not a pass. Today
 * `ProjectWorkspacePage` collapses that into a gap and reports "No territories specified" as a
 * conflict on 49,455 FELT + BELLA_FIGURA tracks whose shelf column was never written by
 * anything. A conflict list that flags every track is a conflict list nobody reads.
 *
 * `missing` is meaningful only for verdict "gap" with a specified request. A specified grant
 * against a worldwide request returns "gap" with `restOfWorld: true` and an empty `missing` —
 * the hole cannot be enumerated.
 */
export function territoryCoverage(
  granted: readonly string[] | null | undefined,
  requested: readonly string[] | null | undefined,
): { verdict: TerritoryCoverageVerdict; missing: string[]; restOfWorld: boolean } {
  const g = territoryScope(granted);
  if (g === "unstated") return { verdict: "unknown", missing: [], restOfWorld: false };
  if (g === "worldwide") return { verdict: "covers", missing: [], restOfWorld: false };

  const r = territoryScope(requested);
  if (r === "unstated") return { verdict: "unknown", missing: [], restOfWorld: false };
  if (r === "worldwide") return { verdict: "gap", missing: [], restOfWorld: true };

  const grantedSet = new Set(normalizeSelection(cleanTokens(granted)));
  const missing = normalizeSelection(cleanTokens(requested)).filter((c) => !grantedSet.has(c));
  return { verdict: missing.length ? "gap" : "covers", missing, restOfWorld: false };
}
